import { ref } from 'vue';
import { Preferences } from '@capacitor/preferences';

export interface NotifItem {
  id: string;
  title: string;
  body: string;
  data: Record<string, string>;
  receivedAt: number;
}

// Module-level singletons — all components share the same reactive state
const notifications = ref<NotifItem[]>([]);
const unreadCount   = ref(0);

export function useNotificationStore() {
  async function init(): Promise<void> {
    const [countRes, listRes] = await Promise.all([
      Preferences.get({ key: 'notif_unread_count' }),
      Preferences.get({ key: 'notif_list' }),
    ]);
    unreadCount.value = parseInt(countRes.value ?? '0', 10) || 0;
    if (listRes.value) {
      try { notifications.value = JSON.parse(listRes.value) as NotifItem[]; } catch { /* ignore */ }
    }
  }

  async function addNotification(item: Omit<NotifItem, 'receivedAt'>): Promise<void> {
    if (notifications.value.some(n => n.id === item.id)) return;
    // Stored verbatim — nothing here rewrites title/body, so whatever shows in the tray is
    // exactly what arrived in the raw push payload (see the '[NOTIF] raw push received' log in
    // App.vue for that).
    console.log('[NOTIF] storing in tray=================>:', item);
    const notif: NotifItem = { ...item, receivedAt: Date.now() };
    notifications.value = [notif, ...notifications.value].slice(0, 50);
    unreadCount.value++;
    await Promise.all([
      Preferences.set({ key: 'notif_unread_count', value: String(unreadCount.value) }),
      Preferences.set({ key: 'notif_list',         value: JSON.stringify(notifications.value) }),
    ]);
  }

  async function markAllRead(): Promise<void> {
    unreadCount.value = 0;
    await Preferences.set({ key: 'notif_unread_count', value: '0' });
  }

  async function clearAll(): Promise<void> {
    notifications.value = [];
    unreadCount.value = 0;
    await Promise.all([
      Preferences.set({ key: 'notif_unread_count', value: '0' }),
      Preferences.set({ key: 'notif_list',         value: '[]' }),
    ]);
  }

  return { notifications, unreadCount, init, addNotification, markAllRead, clearAll };
}
